import { CheckCircle2, X } from 'lucide-react';

interface ForkSuccessBannerProps {
  orphanedInputCount: number;
  onDismiss: () => void;
}

/**
 * Inline banner shown above the chat thread after a successful fork from the
 * MissingModelPanel. Displays the orphaned-input count when > 0 so the user
 * knows some inputs from the previous version didn't carry over (preserved
 * in `agent_state.orphanedInputs`, surfaced in `VariablesPanel`).
 *
 * Dismissed by the X button or by the parent setting `forkBanner` to null
 * (e.g. on the next fork).
 */
export function ForkSuccessBanner({ orphanedInputCount, onDismiss }: ForkSuccessBannerProps) {
  return (
    <div
      className="flex items-start gap-3 bg-accent-dim border-l-4 border-accent/30 rounded-r-md p-4"
      role="status"
    >
      <CheckCircle2 size={18} className="text-accent shrink-0 mt-0.5" />
      <div className="flex-1 text-sm leading-relaxed text-text">
        Forked to the latest model version.{' '}
        {orphanedInputCount > 0 && (
          <>
            {orphanedInputCount} input{orphanedInputCount === 1 ? '' : 's'} from your previous version
            no longer apply and have been preserved in the conversation history but excluded from the
            active model. Review the assumption summary.
          </>
        )}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-text-dim hover:text-text bg-transparent border-none p-1 cursor-pointer"
      >
        <X size={16} />
      </button>
    </div>
  );
}
